import { EXPORT_HEADERS, toExportRow } from '@/lib/isbtCatalog'
import { STATUS_HEADERS, activeCell } from '@/lib/productCodeStatus'
import type { ProductCatalogEntry } from '@/types/products'

/**
 * CSV export of the product catalog — ALL 44 fields, not the 11 the table
 * shows.
 *
 * Pure, so it is unit-tested: the caller fetches the rows and hands them over.
 * `vitest.config.ts` only matches `src/**\/*.test.ts`, which is why the quoting
 * rules live here rather than inside the component that triggers the download.
 */

/**
 * Quote a cell for CSV.
 *
 * Always quoting would be simpler but these strings are the point of the
 * export: an HL7 description is full of `|` and `/` and reads far better
 * unquoted in a diff. So quote only when a comma, quote, or newline makes it
 * necessary — and a leading space, which Excel otherwise eats.
 */
export function csvCell(value: string): string {
  if (value === '') return ''
  const needsQuotes = /[",\r\n]/.test(value) || value !== value.trim()
  if (!needsQuotes) return value
  return `"${value.replace(/"/g, '""')}"`
}

/**
 * The whole file: a header row of the 44 labels, then one row per entry.
 *
 * `withStatus` appends the selected tenant's Active and Note (z8nrz7e72t) —
 * the two columns the status upload reads back. They go LAST so the 44 ISBT
 * columns keep the positions every earlier export had.
 */
export function catalogToCsv(
  entries: (ProductCatalogEntry & { active?: boolean; note?: string })[],
  { withStatus = false }: { withStatus?: boolean } = {},
): string {
  const headers = withStatus ? [...EXPORT_HEADERS, STATUS_HEADERS.active, STATUS_HEADERS.note] : EXPORT_HEADERS
  const lines = [headers.map(csvCell).join(',')]
  for (const entry of entries) {
    const row: Record<string, string> = {
      ...toExportRow(entry),
      ...(withStatus
        ? { [STATUS_HEADERS.active]: activeCell(entry.active), [STATUS_HEADERS.note]: entry.note ?? '' }
        : {}),
    }
    lines.push(headers.map(header => csvCell(row[header] ?? '')).join(','))
  }
  // Trailing newline: POSIX tools treat a file without one as truncated.
  return lines.join('\n') + '\n'
}

/**
 * A filename that says what the export contains.
 *
 * Stamped with the date because these get mailed around and a second copy a
 * week later is otherwise indistinguishable from the first.
 */
export function catalogExportFilename(now = new Date()): string {
  const date = now.toISOString().slice(0, 10)
  return `isbt-product-codes-${date}.csv`
}
